import { computed, ref, toValue, watchEffect, type MaybeRefOrGetter, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { thumbHashToDataURL } from 'thumbhash'
import { useInfoStore } from '@/stores/info.ts'
import { useCatalogSyncStore } from '@/stores/catalog-sync'
import { resolveCachedCoverSrc } from '@/stores/sync/coverCache.ts'
import { coverKeyFromCover, getCoverUrl } from '@/utils/imageUrl.ts'

// Invisible 1x1 — the `src` while the cached file resolves offline. Keeps the img
// transparent (so the thumbHash blur background shows) without the fade/@load
// firing on a placeholder, so the real cover still blurs up when it arrives.
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export function decodeThumbHash(thumbHash: string | null | undefined): string | null {
  if (!thumbHash) return null
  try {
    const bytes = Uint8Array.from(atob(thumbHash), (character) => character.charCodeAt(0))
    return thumbHashToDataURL(bytes)
  } catch {
    return null
  }
}

// `src` = the real cover (remote online, cached file offline). `placeholder` = the
// decoded thumbHash blur when present, rendered under the cover as a blur-up so
// something shows immediately while `src` loads. `hasCover` = whether a cover key
// exists at all; false → the consumer shows a no-cover fallback instead.
export function useCoverSrc(
  cover: MaybeRefOrGetter<string | null | undefined>,
  originalCover?: MaybeRefOrGetter<string | null | undefined>,
  thumbHash?: MaybeRefOrGetter<string | null | undefined>,
): { src: Ref<string>; placeholder: Ref<string>; hasCover: Ref<boolean> } {
  const { connected } = storeToRefs(useInfoStore())
  const { canUseOffline } = storeToRefs(useCatalogSyncStore())
  const src = ref(TRANSPARENT_PIXEL)
  const placeholder = ref('')
  const hasCover = computed(
    () => coverKeyFromCover(toValue(cover), toValue(originalCover)).length > 0,
  )

  watchEffect(async (onCleanup) => {
    // cover/thumbHash can change while the cached-file read below is pending; a
    // superseded run must not assign its (now stale) result over the newer one.
    let cancelled = false
    onCleanup(() => {
      cancelled = true
    })
    const coverValue = toValue(cover)
    const originalCoverValue = originalCover ? toValue(originalCover) : null
    const thumbHashValue = thumbHash ? toValue(thumbHash) : null
    const coverKey = coverKeyFromCover(coverValue, originalCoverValue)
    const remoteUrl = getCoverUrl(coverValue, originalCoverValue)
    const decodedThumbHash = decodeThumbHash(thumbHashValue)
    placeholder.value = decodedThumbHash ?? ''
    const servingLocalReplica = !connected.value && canUseOffline.value

    if (!servingLocalReplica) {
      src.value = remoteUrl
      return
    }

    // Offline the cover comes solely from the budget-encoded offline pack, never a
    // full-res cover cached on-view — so offline images are uniform quality (all
    // pack tier) rather than a mix of pack covers and stray full-res ones. No pack
    // file for this key → stay transparent and let the thumbHash blur stand in.
    // Resolving the cached file is async (Filesystem read); keep src transparent
    // meanwhile so the real cover fades in on load (never a no-cover flash).
    src.value = TRANSPARENT_PIXEL
    const cached = await resolveCachedCoverSrc(coverKey)
    if (cached && !cancelled) src.value = cached
  })

  return { src, placeholder, hasCover }
}
